/**
 * <CancelFlowStep> — la coque d'une étape de la résiliation (template).
 *
 * Le pendant plein écran de `<ParentFlowModal>`, sous la même règle : UN seul en-tête, le
 * `<Header>` des pages — la question de l'étape en titre, le retour à gauche, la croix à droite,
 * et la barre de progression fine à la place du filet (« Étape 2 sur 5 » est dit au lecteur
 * d'écran, pas écrit). Les cinq écrans de la résiliation sont des destinations, pas une
 * feuille — ils gardent donc la coque de page.
 *
 * La tête passe par la fente `header` de `<PageTemplate>`, pas par les enfants : le chargement et
 * l'erreur remplacent les enfants, et une étape qui perd sa croix pendant qu'elle charge enferme
 * le parent dans l'attente.
 *
 * Coquille seule : aucune étape, aucune validation, aucun enchaînement. La page possède l'étape
 * courante et la passe.
 */
import React from "react"

import { Header } from "../../molecules/Header"
import { PageTemplate } from "../PageTemplate"

/** Le parcours fait cinq étapes — la barre de progression les compte toutes, dès la première. */
export const CANCEL_FLOW_STEPS = 5

/**
 * Un parcours en étapes se lit sur une colonne, pas sur le tableau de bord : 560 px au-delà de
 * 800, la largeur que le legacy tenait déjà (`CancelReasonScreen.js:23-26`). Les 1120 px des
 * pages parent conviennent à deux colonnes de cartes, pas à une question et deux boutons.
 */
const CANCEL_FLOW_WIDTH = 560

export type CancelFlowStepProps = {
    /** Titre de l'étape courante, affiché dans l'en-tête. */
    stepTitle: string
    step: number
    /** Nombre d'étapes — cinq, sauf story. */
    totalSteps?: number
    /** Absent → pas de flèche de retour. */
    onBack?: () => void
    /** Absent → pas de croix de sortie. */
    onClose?: () => void
    loading?: boolean
    /** La forme du corps de l'étape pendant son chargement — la tête, elle, reste. */
    skeleton?: React.ReactNode
    error?: unknown
    onRetry?: () => void
    children: React.ReactNode
    testID?: string
}

export function CancelFlowStep(props: CancelFlowStepProps) {
    const {
        stepTitle,
        step,
        totalSteps = CANCEL_FLOW_STEPS,
        onBack,
        onClose,
        loading = false,
        skeleton,
        error,
        onRetry,
        children,
        testID,
    } = props

    const header = <Header title={stepTitle} steps={{ step, total: totalSteps, onBack, onClose }} />

    return (
        <PageTemplate
            testID={testID}
            header={header}
            loading={loading}
            skeleton={skeleton}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={CANCEL_FLOW_WIDTH}
        >
            {children}
        </PageTemplate>
    )
}
